import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import GuardedRoute from '@/guard/GuardedRoute';
import { useAppContext } from '@/context/AppContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import PortalLayout from '@/components/layout/PortalLayout';
import { pageAction, pageEdit, pageView } from '@malash-office/types';

const Login = lazy(() => import('@/pages/auth/Login'));
const SupplierQuotePage = lazy(() => import('@/pages/public/SupplierQuote'));
const SupplierShipmentPage = lazy(() => import('@/pages/public/SupplierShipment'));
const QuoteViewPage = lazy(() => import('@/pages/public/QuoteView'));
const QuoteRequestPage = lazy(() => import('@/pages/public/QuoteRequest'));
const CheckoutPage = lazy(() => import('@/pages/public/Checkout'));
const CheckoutDonePage = lazy(() => import('@/pages/public/CheckoutDone'));
const Home = lazy(() => import('@/pages/dashboard/Home'));
const Users = lazy(() => import('@/pages/dashboard/Users'));
const DashboardUsers = lazy(() => import('@/pages/dashboard/DashboardUsers/List'));
const DashboardUserCard = lazy(() => import('@/pages/dashboard/DashboardUsers/UserCard'));
const DashboardUsersActivities = lazy(() => import('@/pages/dashboard/DashboardUsers/Activities'));
const DashboardUsersMailingHistory = lazy(() => import('@/pages/dashboard/DashboardUsers/MailingHistory'));
const DashboardUsersCompleteness = lazy(() => import('@/pages/dashboard/DashboardUsers/Completeness'));
const DashboardUsersGroupCompare = lazy(() => import('@/pages/dashboard/DashboardUsers/GroupCompare'));
const Roles = lazy(() => import('@/pages/dashboard/Roles'));
const MailConnections = lazy(() => import('@/pages/dashboard/MailConnections'));
const Notifications = lazy(() => import('@/pages/dashboard/Notifications'));
const FinanceOverview = lazy(() => import('@/pages/dashboard/Finance/Overview'));
const FinanceCollection = lazy(() => import('@/pages/dashboard/Finance/Collection'));
const Customers = lazy(() => import('@/pages/dashboard/Finance/Customers'));
const CustomerCard = lazy(() => import('@/pages/dashboard/Finance/CustomerCard'));
const Payments = lazy(() => import('@/pages/dashboard/Finance/Payments'));
const Subscriptions = lazy(() => import('@/pages/dashboard/Products/Subscriptions'));
const SubscriptionPlans = lazy(() => import('@/pages/dashboard/Products/SubscriptionPlans'));
const LandingPages = lazy(() => import('@/pages/dashboard/Products/LandingPages'));
const LandingPageEditor = lazy(() => import('@/pages/dashboard/Products/LandingPageEditor'));
const SubscriptionCard = lazy(() => import('@/pages/dashboard/Products/SubscriptionCard'));
const ProductList = lazy(() => import('@/pages/dashboard/Products/ProductList'));
const ProductWizard = lazy(() => import('@/pages/dashboard/Products/ProductWizard'));
const Taxonomy = lazy(() => import('@/pages/dashboard/Products/Taxonomy'));
const ProductEdit = lazy(() => import('@/pages/dashboard/Products/ProductEdit'));
const ProductsOverview = lazy(() => import('@/pages/dashboard/Products/Overview'));
const Integrations = lazy(() => import('@/pages/dashboard/Products/Integrations'));
const Stores = lazy(() => import('@/pages/dashboard/Products/Stores'));
const StoreCard = lazy(() => import('@/pages/dashboard/Products/StoreCard'));
const Pricelists = lazy(() => import('@/pages/dashboard/Products/Pricelists'));
const Inventory = lazy(() => import('@/pages/dashboard/Products/Inventory'));
const Warehouses = lazy(() => import('@/pages/dashboard/Products/Warehouses'));
const InventoryProductCard = lazy(() => import('@/pages/dashboard/Products/InventoryProductCard'));
const Fulfillment = lazy(() => import('@/pages/dashboard/Products/Fulfillment'));
const Promotions = lazy(() => import('@/pages/dashboard/Products/Promotions'));
const PromotionCard = lazy(() => import('@/pages/dashboard/Products/PromotionCard'));
const PromotionWizard = lazy(() => import('@/pages/dashboard/Products/PromotionWizard'));
const Quotes = lazy(() => import('@/pages/dashboard/Products/Quotes'));
const QuoteBuilder = lazy(() => import('@/pages/dashboard/Products/QuoteBuilder'));
const QuotePageEditor = lazy(() => import('@/pages/dashboard/Products/QuotePageEditor'));
const SubscriptionOrders = lazy(() => import('@/pages/dashboard/Warehouse/SubscriptionOrders'));
const OrdersPacking = lazy(() => import('@/pages/dashboard/Warehouse/OrdersPacking'));
const Orders = lazy(() => import('@/pages/dashboard/Warehouse/Orders'));
const OrderCard = lazy(() => import('@/pages/dashboard/Warehouse/OrderCard'));
const Picking = lazy(() => import('@/pages/dashboard/Warehouse/Picking'));
const PickOrder = lazy(() => import('@/pages/dashboard/Warehouse/PickOrder'));
const OrderMailing = lazy(() => import('@/pages/dashboard/CustomerService/OrderMailing'));
const Inbox = lazy(() => import('@/pages/dashboard/Communication/Inbox'));
const WhatsAppTemplates = lazy(() => import('@/pages/dashboard/Communication/Templates'));
const Broadcasts = lazy(() => import('@/pages/dashboard/Communication/Broadcasts'));
const BroadcastWizard = lazy(() => import('@/pages/dashboard/Communication/BroadcastWizard'));
const BroadcastCard = lazy(() => import('@/pages/dashboard/Communication/BroadcastCard'));
const PlanningProjectList = lazy(() => import('@/pages/dashboard/Planning/ProjectList'));
const PlanningProjectCard = lazy(() => import('@/pages/dashboard/Planning/ProjectCard'));
const PlanningMyTasks = lazy(() => import('@/pages/dashboard/Planning/MyTasks'));
const PlanningFileLibrary = lazy(() => import('@/pages/dashboard/Planning/FileLibrary'));
const PlanningChat = lazy(() => import('@/pages/dashboard/Planning/Chat'));
const PlanningTeam = lazy(() => import('@/pages/dashboard/Planning/Team'));
const PlanningBudget = lazy(() => import('@/pages/dashboard/Planning/Budget'));
const ProductionCustomers = lazy(() => import('@/pages/dashboard/Planning/ProductionCustomers'));
const PlanningSuppliers = lazy(() => import('@/pages/dashboard/Planning/Suppliers'));
const ProductionOrderList = lazy(() => import('@/pages/dashboard/Production/OrderList'));
const ProductionOrderCard = lazy(() => import('@/pages/dashboard/Production/OrderCard'));
const ProductionBudget = lazy(() => import('@/pages/dashboard/Production/Budget'));
const SupplierShipments = lazy(() => import('@/pages/dashboard/Production/Shipments'));
const SupplierInvoices = lazy(() => import('@/pages/dashboard/Production/SupplierInvoices'));
const FeedbackCenter = lazy(() => import('@/pages/dashboard/Management/Feedback'));
const CreditExpenses = lazy(() => import('@/pages/dashboard/Management/CreditExpenses'));
const GoogleConnectReturn = lazy(() => import('@/pages/dashboard/Mailing/GoogleConnectReturn'));
const PortalOrderList = lazy(() => import('@/pages/portal/OrderList'));
const PortalOrderNew = lazy(() => import('@/pages/portal/OrderNew'));
const PortalOrderCard = lazy(() => import('@/pages/portal/OrderCard'));
const PortalOrderEdit = lazy(() => import('@/pages/portal/OrderEdit'));

const Loader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <CircularProgress />
  </Box>
);

/** Keeps the query string across a moved route — those old links carry the grid's filters. */
const RedirectPreservingSearch = ({ to }: { to: string }) => (
  <Navigate to={{ pathname: to, search: useLocation().search }} replace />
);

const guarded = (element: JSX.Element, permission?: string) => (
  <GuardedRoute permission={permission}>
    <DashboardLayout>{element}</DashboardLayout>
  </GuardedRoute>
);

/**
 * The customer portal's three screens.
 *
 * A second shell rather than `DashboardLayout` with a filtered menu: three of `NAV_ITEMS`'s
 * top-level groups carry no permission at all and would render for an external customer. `portal`
 * on the guard makes the two shells mutually exclusive in both directions — see `GuardedRoute`.
 */
const portalGuarded = (element: JSX.Element) => (
  <GuardedRoute portal>
    <PortalLayout>{element}</PortalLayout>
  </GuardedRoute>
);

/** The catch-all's target, which only the context can answer. */
const HomeRedirect = () => <Navigate to={useAppContext().homePath} replace />;

const App = () => (
  <Suspense fallback={<Loader />}>
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* A supplier opening a quote link has no account. The token is the credential, so this
          route is not GuardedRoute and must stay outside the dashboard shell. */}
      <Route path="/supplier-quote/:token" element={<SupplierQuotePage />} />
      {/* The supplier's shipment link — same rule: the token is the credential, no dashboard shell. */}
      <Route path="/supplier-shipment/:token" element={<SupplierShipmentPage />} />
      {/* הצעות מחיר: the customer's quote (the token is the credential) and a published request
          page. Same rule as the supplier link — no GuardedRoute, no dashboard shell. */}
      <Route path="/q/:token" element={<QuoteViewPage />} />
      <Route path="/quote-request/:slug" element={<QuoteRequestPage />} />
      {/* דפי נחיתה: the hosted checkout of a published page. The slug is the credential, and the id of a
          checkout the only one for its thank-you page — no GuardedRoute, no dashboard shell. */}
      <Route path="/checkout/:slug" element={<CheckoutPage />} />
      <Route path="/checkout/:slug/done" element={<CheckoutDonePage />} />

      {/* פורטל לקוחות ייצור. '/new' is declared before '/orders/:id' for the reason every other
          block here states — react-router matches in order. */}
      <Route path="/portal" element={portalGuarded(<PortalOrderList />)} />
      <Route path="/portal/new" element={portalGuarded(<PortalOrderNew />)} />
      <Route path="/portal/orders/:id/edit" element={portalGuarded(<PortalOrderEdit />)} />
      <Route path="/portal/orders/:id" element={portalGuarded(<PortalOrderCard />)} />
      <Route path="/" element={guarded(<Home />)} />
      <Route path="/users" element={guarded(<Users />, pageView('users'))} />
      {/* משתמשי דשבורד (DASHBOARD-USERS.md). The static paths before '/:id', as everywhere here. */}
      <Route path="/dashboard-users" element={guarded(<DashboardUsers />, pageView('dashboard-users'))} />
      <Route
        path="/dashboard-users/activities"
        element={guarded(<DashboardUsersActivities />, pageView('dashboard-users'))}
      />
      <Route
        path="/dashboard-users/mailing-history"
        element={guarded(<DashboardUsersMailingHistory />, pageView('dashboard-users'))}
      />
      <Route
        path="/dashboard-users/completeness"
        element={guarded(<DashboardUsersCompleteness />, pageView('dashboard-users'))}
      />
      <Route
        path="/dashboard-users/group-compare"
        element={guarded(<DashboardUsersGroupCompare />, pageView('dashboard-users'))}
      />
      <Route path="/dashboard-users/:id" element={guarded(<DashboardUserCard />, pageView('dashboard-users'))} />
      <Route path="/roles" element={guarded(<Roles />, pageView('roles'))} />
      {/* חיבור מיילים replaced כתובות שולח and אגפים ומיילים; their old links land on its tabs. */}
      <Route path="/mail-connections" element={guarded(<MailConnections />, pageView('mail-connections'))} />
      <Route path="/notifications" element={guarded(<Notifications />, pageView('push-notifications'))} />
      <Route path="/sender-addresses" element={<Navigate to="/mail-connections?tab=departments" replace />} />
      {/*
        Where Google sends the browser back after *every* connection — the address registered on the
        OAuth client (docs/product-planning/MAIL-CONNECTIONS.md §4). A bare page with no data, open to
        every signed-in user: an employee connecting their own mailbox must not need, or see, the
        management screen. The server decides what each kind of connection may do.
      */}
      <Route path="/mailing/connect-email" element={guarded(<GoogleConnectReturn />)} />
      <Route path="/communication/inbox" element={guarded(<Inbox />, pageView('inbox'))} />
      <Route path="/communication/templates" element={guarded(<WhatsAppTemplates />, pageView('whatsapp-templates'))} />
      <Route path="/customer-service/order-mailing" element={guarded(<OrderMailing />, pageView('order-mailing'))} />
      <Route path="/communication/broadcasts" element={guarded(<Broadcasts />, pageView('broadcasts'))} />
      {/* '/new' before '/:id', the same order as every other card in this file. */}
      <Route path="/communication/broadcasts/new" element={guarded(<BroadcastWizard />, pageEdit('broadcasts'))} />
      <Route path="/communication/broadcasts/:id/edit" element={guarded(<BroadcastWizard />, pageEdit('broadcasts'))} />
      <Route path="/communication/broadcasts/:id" element={guarded(<BroadcastCard />, pageView('broadcasts'))} />
      <Route
        path="/customer-service/order-mailing/:tab"
        element={guarded(<OrderMailing />, pageView('order-mailing'))}
      />
      {/*
        תכנון ופיתוח מוצרים. Every literal segment is declared **before** `/planning/projects/:id`,
        for the reason the Product Hub block below states: react-router matches in order, and
        `/planning/tasks` would otherwise be read as a project card with 'tasks' for an id.
        `/planning/customers` carries its own permission — לקוחות ייצור is not the planning module's
        data, and somebody may hold one without the other.
      */}
      <Route path="/planning" element={guarded(<PlanningProjectList />, pageView('planning-projects'))} />
      <Route path="/planning/tasks" element={guarded(<PlanningMyTasks />, pageView('planning-projects'))} />
      <Route path="/planning/chat" element={guarded(<PlanningChat />, pageView('planning-projects'))} />
      <Route path="/planning/files" element={guarded(<PlanningFileLibrary />, pageView('planning-files'))} />
      <Route path="/planning/team" element={guarded(<PlanningTeam />, pageView('planning-projects'))} />
      <Route path="/planning/budget" element={guarded(<PlanningBudget />, pageAction('planning-projects', 'budget'))} />
      <Route path="/planning/customers" element={guarded(<ProductionCustomers />, pageView('production-customers'))} />
      <Route path="/planning/suppliers" element={guarded(<PlanningSuppliers />, pageView('suppliers'))} />
      <Route path="/planning/projects/:id" element={guarded(<PlanningProjectCard />, pageView('planning-projects'))} />

      {/*
        יצור ורכש — הזמנות ספקים ושילוח ספקים. `/production` itself has never resolved to a screen and still
        does not: the module's pages are `/production/orders` and `/production/budget`, and a bare
        `/production` falls through to the catch-all like every other unbuilt slot. `:id` is
        declared last so a literal segment added here later is not read as an order id.
      */}
      <Route path="/production/orders" element={guarded(<ProductionOrderList />, pageView('production-orders'))} />
      <Route
        path="/production/budget"
        element={guarded(<ProductionBudget />, pageAction('production-orders', 'budget'))}
      />
      <Route path="/production/shipments" element={guarded(<SupplierShipments />, pageView('supplier-shipments'))} />
      {/* חשבוניות ספקים. The tab is a path segment, never `?tab=`: GridData's Filters would read it as a filter. */}
      <Route path="/management/feedback" element={guarded(<FeedbackCenter />, 'feedback:submit')} />
      <Route path="/management/credit-expenses" element={guarded(<CreditExpenses />, 'credit-expenses:submit')} />
      <Route path="/production/invoices" element={guarded(<SupplierInvoices />, pageView('supplier-invoices'))} />
      <Route path="/production/invoices/:tab" element={guarded(<SupplierInvoices />, pageView('supplier-invoices'))} />
      <Route path="/production/orders/:id" element={guarded(<ProductionOrderCard />, pageView('production-orders'))} />

      <Route path="/finance/overview" element={guarded(<FinanceOverview />, pageView('finance-overview'))} />
      <Route path="/finance/collection" element={guarded(<FinanceCollection />, pageView('collection'))} />
      <Route path="/finance/customers" element={guarded(<Customers />, pageView('customers'))} />
      <Route path="/finance/customers/:id" element={guarded(<CustomerCard />, pageView('customers'))} />
      <Route path="/finance/payments" element={guarded(<Payments />, pageView('payments'))} />
      {/* '/new' is declared before '/:id' so it is never read as a subscription id. */}
      {/*
        Product Hub. Tabs inside these screens are path segments, never `?tab=` — GridData's
        Filters treats any unrecognised query key as an active filter, and its "clear" button
        deletes it (a live bug on /warehouse/subscription-orders?tab=credits).
      */}
      <Route path="/products/overview" element={guarded(<ProductsOverview />, pageView('products-overview'))} />
      <Route path="/products/catalog" element={guarded(<ProductList />, pageView('products'))} />
      {/* '/new' and '/taxonomy' before '/:id' so neither is ever read as a product id. The card's
          tab is a path segment. */}
      <Route path="/products/catalog/new" element={guarded(<ProductWizard />, pageEdit('products'))} />
      <Route path="/products/catalog/taxonomy" element={guarded(<Taxonomy />, pageView('taxonomy'))} />
      <Route path="/products/catalog/taxonomy/:tab" element={guarded(<Taxonomy />, pageView('taxonomy'))} />
      <Route path="/products/catalog/:id" element={guarded(<ProductEdit />, pageView('products'))} />
      {/* The card's six tabs became cards of one form. The old addresses still resolve — they open
          the matching card instead of a tab, so every link elsewhere in the module still lands
          where it meant to. */}
      <Route path="/products/catalog/:id/:tab" element={guarded(<ProductEdit />, pageView('products'))} />
      <Route path="/products/integrations" element={guarded(<Integrations />, pageView('integrations'))} />
      <Route path="/products/stores" element={guarded(<Stores />, pageView('stores'))} />
      {/* The tab is a path segment, and the segment-less form falls back to the first tab. */}
      <Route path="/products/stores/:id" element={guarded(<StoreCard />, pageView('stores'))} />
      <Route path="/products/stores/:id/:tab" element={guarded(<StoreCard />, pageView('stores'))} />
      <Route path="/products/pricelists" element={guarded(<Pricelists />, pageView('pricelists'))} />

      {/*
        Inventory. `/items/:productId` is the product's inventory card and is declared **before** the
        `/:tab` route, so 'items' is not swallowed as a tab with a stray segment after it. The
        segment-less form falls back to the first tab.
      */}
      <Route
        path="/products/inventory/items/:productId"
        element={guarded(<InventoryProductCard />, pageView('inventory'))}
      />
      {/* מחסנים left the inventory tabs for a menu entry of its own. The old tab URL is still in
          bookmarks and in links written before the split, so it redirects instead of falling through
          to `/:tab` (which would silently show the overview) — and it keeps the query string, since
          those links carry filters. */}
      <Route path="/products/inventory/warehouses" element={<RedirectPreservingSearch to="/products/warehouses" />} />
      <Route path="/products/inventory" element={guarded(<Inventory />, pageView('inventory'))} />
      <Route path="/products/inventory/:tab" element={guarded(<Inventory />, pageView('inventory'))} />

      <Route path="/products/warehouses" element={guarded(<Warehouses />, pageView('warehouses'))} />

      {/* Shipping & fulfillment. The tab is a path segment; the segment-less form falls back. */}
      <Route path="/products/fulfillment" element={guarded(<Fulfillment />, pageView('fulfillment'))} />
      <Route path="/products/fulfillment/:tab" element={guarded(<Fulfillment />, pageView('fulfillment'))} />

      {/* '/new' before '/:id' so it is never read as a promotion id. */}
      <Route path="/products/promotions" element={guarded(<Promotions />, pageView('promotions'))} />
      <Route path="/products/promotions/new" element={guarded(<PromotionWizard />, pageEdit('promotions'))} />
      <Route path="/products/promotions/:id" element={guarded(<PromotionCard />, pageView('promotions'))} />
      {/* '/pages' and '/new' before '/:id' — react-router matches in order. The list's two tabs are
          two paths on one component. */}
      <Route path="/products/quotes" element={guarded(<Quotes />, pageView('quotes'))} />
      <Route path="/products/quotes/pages" element={guarded(<Quotes />, pageView('quotes'))} />
      <Route path="/products/quotes/pages/new" element={guarded(<QuotePageEditor />, pageEdit('quotes'))} />
      <Route path="/products/quotes/pages/:id" element={guarded(<QuotePageEditor />, pageView('quotes'))} />
      <Route path="/products/quotes/new" element={guarded(<QuoteBuilder />, pageEdit('quotes'))} />
      <Route path="/products/quotes/:id" element={guarded(<QuoteBuilder />, pageView('quotes'))} />

      <Route path="/products/subscriptions" element={guarded(<Subscriptions />, pageView('subscriptions'))} />
      <Route path="/products/subscriptions/plans" element={guarded(<SubscriptionPlans />, pageEdit('subscriptions'))} />
      <Route path="/products/subscriptions/new" element={guarded(<SubscriptionCard />, pageEdit('subscriptions'))} />
      <Route path="/products/subscriptions/:id" element={guarded(<SubscriptionCard />, pageView('subscriptions'))} />
      <Route path="/products/landing-pages" element={guarded(<LandingPages />, pageView('landing-pages'))} />
      <Route path="/products/landing-pages/new" element={guarded(<LandingPageEditor />, pageEdit('landing-pages'))} />
      <Route path="/products/landing-pages/:id" element={guarded(<LandingPageEditor />, pageView('landing-pages'))} />
      {/* Store orders. The list, then one order — declared before the subscription-order routes so
          the two families read together, and '/:id' after the list so it is never read as a segment. */}
      <Route path="/warehouse/orders" element={guarded(<Orders />, pageView('orders'))} />
      <Route path="/warehouse/orders/:id" element={guarded(<OrderCard />, pageView('orders'))} />
      {/* Picking is its own page and not part of הזמנות: §34 gives the warehouse its own permission,
          and a picker who could open הזמנות would get the manager's screen with the prices §1
          forbids showing them. */}
      <Route path="/warehouse/picking" element={guarded(<Picking />, pageView('picking'))} />
      <Route path="/warehouse/picking/:orderId" element={guarded(<PickOrder />, pageView('picking'))} />
      {/* '/packing' is declared before the list route only for readability — the paths don't overlap. */}
      <Route
        path="/warehouse/subscription-orders/packing"
        element={guarded(<OrdersPacking />, pageView('subscription-orders'))}
      />
      <Route
        path="/warehouse/subscription-orders"
        element={guarded(<SubscriptionOrders />, pageView('subscription-orders'))}
      />
      {/* Home depends on who is asking: a customer who types an address that does not exist must
          land in the portal, not on the team's home screen — which greets them by name and prints
          the list of permissions they do not have. */}
      <Route path="*" element={<HomeRedirect />} />
    </Routes>
  </Suspense>
);

export default App;
